import { makeStyles } from "@material-ui/core/styles";
import React, { useEffect, useMemo, useState } from "react";
import { isAuthenticated } from "./components/utils/utils";
import { useHistory } from "react-router-dom";
import { unoTheme } from "./assets/styles/unoTheme";
import { Bar, Doughnut } from "react-chartjs-2";
import {
    Chip,
    FormControl,
    IconButton,
    MenuItem,
    Select,
    TextField,
    Tooltip,
} from "@material-ui/core";
import ClearIcon from "@material-ui/icons/Clear";

// ───── Constants ──────────────────────────────────────────────────────────
const COLORS = {
    Added: "#2ecc71",
    Changed: "#e67e22",
    Fixed: "#e74c3c",
    Support: "#3498db",
};

const TYPE_ICONS = { Added: "✅", Changed: "🔄", Fixed: "🐛", Support: "🛠️" };

const MONTH_NAMES = [
    "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
    "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

const MONTH_SHORT = [
    "Jan", "Fev", "Mar", "Abr", "Mai", "Jun",
    "Jul", "Ago", "Set", "Out", "Nov", "Dez",
];

// ───── Styles ─────────────────────────────────────────────────────────────
const useStyles = makeStyles(() => ({
    root: {
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        background: "#f0f2f5",
        fontFamily: "'Manrope', 'Roboto', sans-serif",
        overflow: "hidden",
    },
    header: {
        padding: "16px 28px",
        background: "white",
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        display: "flex",
        alignItems: "center",
        gap: "24px",
        flexWrap: "wrap",
        zIndex: 10,
        flexShrink: 0,
    },
    title: {
        fontSize: 20,
        fontWeight: 800,
        color: unoTheme.mainColor,
        letterSpacing: "-0.3px",
        flexShrink: 0,
    },
    headerDivider: {
        width: 1,
        height: 28,
        background: "#e4e7eb",
        flexShrink: 0,
    },
    filterLabel: {
        fontSize: 10,
        fontWeight: 700,
        color: unoTheme.secondaryColor,
        textTransform: "uppercase",
        letterSpacing: "0.7px",
        flexShrink: 0,
    },
    filterGroup: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
    },
    typeChip: {
        fontWeight: 700,
        fontSize: 12,
        height: 30,
        cursor: "pointer",
        transition: "opacity 0.15s, transform 0.1s",
        "&:hover": { opacity: 0.85, transform: "translateY(-1px)" },
        "& .MuiChip-label": { padding: "0 12px" },
    },
    periodSelect: {
        "& .MuiOutlinedInput-input": {
            padding: "7px 32px 7px 12px",
            fontSize: 13,
            fontWeight: 600,
        },
    },
    yearSelect: {
        minWidth: 90,
    },
    monthSelect: {
        minWidth: 140,
    },
    dateInput: {
        "& .MuiOutlinedInput-input": {
            padding: "7px 10px",
            fontSize: 13,
            fontWeight: 600,
        },
        minWidth: 145,
    },
    body: {
        display: "flex",
        flex: 1,
        overflow: "hidden",
    },
    leftPanel: {
        flex: "0 0 58%",
        overflowY: "auto",
        padding: "20px 20px 20px 24px",
        borderRight: "1px solid #e4e7eb",
        textAlign: "left",
    },
    rightPanel: {
        flex: "0 0 42%",
        overflowY: "auto",
        padding: "20px 24px 20px 20px",
        background: "#f7f9fc",
        display: "flex",
        flexDirection: "column",
        gap: "20px",
    },
    versionGroup: {
        marginBottom: 20,
    },
    versionBadge: {
        display: "inline-flex",
        alignItems: "center",
        background: unoTheme.mainColor,
        color: "white",
        padding: "5px 16px",
        borderRadius: 24,
        fontWeight: 700,
        fontSize: 13,
        marginBottom: 10,
        boxShadow: "0 2px 8px rgba(0,73,198,0.25)",
        letterSpacing: "0.2px",
    },
    changeItem: {
        background: "white",
        borderRadius: 10,
        padding: "9px 14px 9px 0",
        marginBottom: 7,
        boxShadow: "0 1px 3px rgba(0,0,0,0.07)",
        display: "flex",
        alignItems: "center",
        overflow: "hidden",
        transition: "box-shadow 0.18s",
        "&:hover": {
            boxShadow: "0 3px 10px rgba(0,0,0,0.13)",
        },
    },
    itemSidebar: {
        width: 5,
        alignSelf: "stretch",
        flexShrink: 0,
        borderRadius: "10px 0 0 10px",
        marginRight: 12,
    },
    typePill: {
        fontSize: 10,
        fontWeight: 800,
        padding: "2px 8px",
        borderRadius: 10,
        color: "white",
        marginRight: 8,
        flexShrink: 0,
        letterSpacing: "0.4px",
        lineHeight: "16px",
    },
    itemText: {
        flex: 1,
        fontSize: 13,
        color: "#2d3748",
        lineHeight: 1.5,
    },
    noItems: {
        textAlign: "center",
        color: unoTheme.secondaryColor,
        padding: "48px 0",
        fontSize: 14,
    },
    statsRow: {
        display: "flex",
        gap: "12px",
    },
    statCard: {
        flex: 1,
        background: "white",
        borderRadius: 10,
        padding: "14px 8px",
        boxShadow: "0 1px 4px rgba(0,0,0,0.09)",
        textAlign: "center",
    },
    statNumber: {
        fontSize: 26,
        fontWeight: 800,
        lineHeight: 1.1,
    },
    statLabel: {
        fontSize: 10,
        fontWeight: 700,
        color: unoTheme.secondaryColor,
        marginTop: 3,
        textTransform: "uppercase",
        letterSpacing: "0.5px",
    },
    chartCard: {
        background: "white",
        borderRadius: 12,
        padding: "16px 18px",
        boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
    },
    highlightChip: {
        fontWeight: 700,
        fontSize: 12,
        height: 30,
        cursor: "pointer",
        transition: "opacity 0.15s, transform 0.1s",
        "&:hover": { opacity: 0.85, transform: "translateY(-1px)" },
        "& .MuiChip-label": { padding: "0 12px" },
    },
    highlightBadge: {
        fontSize: 13,
        marginLeft: 6,
        flexShrink: 0,
    },
    changeItemHighlighted: {
        background: "white",
        borderRadius: 10,
        padding: "9px 14px 9px 0",
        marginBottom: 7,
        boxShadow: "0 1px 6px rgba(255,193,7,0.45)",
        display: "flex",
        alignItems: "center",
        overflow: "hidden",
        transition: "box-shadow 0.18s",
        border: "1.5px solid #ffd54f",
        "&:hover": {
            boxShadow: "0 3px 12px rgba(255,193,7,0.6)",
        },
    },
    changeItemMega: {
        background: "#fffdf2",
        borderRadius: 10,
        padding: "9px 14px 9px 0",
        marginBottom: 7,
        boxShadow: "0 2px 10px rgba(249,168,37,0.5)",
        display: "flex",
        alignItems: "center",
        overflow: "hidden",
        transition: "box-shadow 0.18s",
        border: "2.5px solid #f9a825",
        "&:hover": {
            boxShadow: "0 4px 16px rgba(249,168,37,0.65)",
        },
    },
    itemTextMega: {
        fontWeight: 600,
    },
    chartTitle: {
        fontSize: 11,
        fontWeight: 700,
        color: unoTheme.secondaryColor,
        marginBottom: 14,
        textTransform: "uppercase",
        letterSpacing: "0.6px",
    },
}));

// ───── Parser ─────────────────────────────────────────────────────────────
function parseChangelog(md) {
    const lines = md.split("\n");
    const entries = [];
    let currentVersion = null;
    let currentDate = null;
    let currentFullDate = null;
    let currentSection = null;

    for (const line of lines) {
        if (line.startsWith("## ")) {
            const h2 = line.replace(/^##\s+/, "");
            // Format: "DD-MM-YYYY"  →  store as "YYYY-MM" and "YYYY-MM-DD"
            const dateMatch = h2.match(/(\d{2})-(\d{2})-(\d{4})/);
            currentVersion = h2;
            currentDate = dateMatch ? `${dateMatch[3]}-${dateMatch[2]}` : null;
            currentFullDate = dateMatch ? `${dateMatch[3]}-${dateMatch[2]}-${dateMatch[1]}` : null;
            currentSection = null;
            continue;
        }
        if (line.startsWith("### ")) {
            const sec = line.replace(/^###\s+/, "").trim();
            currentSection = ["Added", "Changed", "Fixed", "Support"].includes(sec) ? sec : null;
            continue;
        }
        if ((line.startsWith("- ") || line.startsWith("• ")) && currentVersion) {
            const text = line.replace(/^(-|•)\s+/, "");
            const tagMatch = text.match(/^\[(Added|Changed|Fixed|Support)\](!{0,2})\s*(.*)/);
            const type = tagMatch ? tagMatch[1] : currentSection;
            const highlightLevel = tagMatch ? tagMatch[2].length : 0;
            entries.push({ version: currentVersion, date: currentDate, fullDate: currentFullDate, type, highlightLevel, text });
        }
    }
    return entries;
}

function formatMonth(ym) {
    if (!ym) return "Sem data";
    const [year, month] = ym.split("-");
    return `${MONTH_SHORT[parseInt(month, 10) - 1]}/${year}`;
}

function stripTag(text) {
    const m = text.match(/^\[(Added|Changed|Fixed|Support)\]!{0,2}\s*(.*)/);
    return m ? m[2] : text;
}

// ───── Charts ─────────────────────────────────────────────────────────────
function DonutChart({ data, classes }) {
    const counts = {
        Added: data.filter((e) => e.type === "Added").length,
        Changed: data.filter((e) => e.type === "Changed").length,
        Fixed: data.filter((e) => e.type === "Fixed").length,
        Support: data.filter((e) => e.type === "Support").length,
    };
    const total = counts.Added + counts.Changed + counts.Fixed + counts.Support;
    if (!total) return null;

    const chartData = {
        labels: ["Added", "Changed", "Fixed", "Support"],
        datasets: [{
            data: [counts.Added, counts.Changed, counts.Fixed, counts.Support],
            backgroundColor: [COLORS.Added, COLORS.Changed, COLORS.Fixed, COLORS.Support],
            borderWidth: 0,
        }],
    };

    return (
        <div className={classes.chartCard}>
            <div className={classes.chartTitle}>Distribuição por tipo</div>
            <Doughnut
                data={chartData}
                options={{
                    cutoutPercentage: 65,
                    legend: {
                        position: "bottom",
                        labels: { fontFamily: "inherit", fontSize: 12, padding: 14 },
                    },
                    tooltips: {
                        callbacks: {
                            label: (item, d) => {
                                const val = d.datasets[0].data[item.index];
                                const pct = total ? Math.round((val / total) * 100) : 0;
                                return ` ${d.labels[item.index]}: ${val} (${pct}%)`;
                            },
                        },
                    },
                    maintainAspectRatio: true,
                }}
            />
        </div>
    );
}

function BarChart({ data, classes, months }) {
    const visible = months.filter(Boolean).sort();
    if (!visible.length) return null;

    const countByMonthType = (type) =>
        visible.map((m) => data.filter((e) => e.date === m && e.type === type).length);

    const chartData = {
        labels: visible.map(formatMonth),
        datasets: [
            { label: "Added", backgroundColor: COLORS.Added, data: countByMonthType("Added") },
            { label: "Changed", backgroundColor: COLORS.Changed, data: countByMonthType("Changed") },
            { label: "Fixed", backgroundColor: COLORS.Fixed, data: countByMonthType("Fixed") },
            { label: "Support", backgroundColor: COLORS.Support, data: countByMonthType("Support") },
        ],
    };

    return (
        <div className={classes.chartCard}>
            <div className={classes.chartTitle}>Alterações por mês</div>
            <Bar
                data={chartData}
                options={{
                    scales: {
                        xAxes: [{ stacked: true, ticks: { fontSize: 11, fontFamily: "inherit" } }],
                        yAxes: [{ stacked: true, ticks: { beginAtZero: true, stepSize: 1, fontFamily: "inherit" } }],
                    },
                    legend: {
                        position: "bottom",
                        labels: { fontFamily: "inherit", fontSize: 12, padding: 14 },
                    },
                    maintainAspectRatio: true,
                }}
            />
        </div>
    );
}

// ───── Main Component ─────────────────────────────────────────────────────
export default function UnoChangelog() {
    const classes = useStyles();
    const history = useHistory();
    const [rawText, setRawText] = useState("");
    const [selectedYear, setSelectedYear] = useState("all");
    const [selectedMonth, setSelectedMonth] = useState("all");
    const [selectedTypes, setSelectedTypes] = useState(["Added", "Changed", "Fixed", "Support"]);
    const [showOnlyHighlights, setShowOnlyHighlights] = useState(false);
    const [showOnlyMega, setShowOnlyMega] = useState(false);
    const [dateRangeStart, setDateRangeStart] = useState("");
    const [dateRangeEnd, setDateRangeEnd] = useState("");

    useEffect(() => {
        if (!isAuthenticated()) history.push("/login");
    }, [history]);

    useEffect(() => {
        fetch("/CHANGELOG.md")
            .then((r) => r.text())
            .then(setRawText)
            .catch(() => setRawText(""));
    }, []);

    const allEntries = useMemo(() => parseChangelog(rawText), [rawText]);

    // All unique years, descending
    const allYears = useMemo(() => {
        const set = new Set(allEntries.map((e) => e.date ? e.date.split("-")[0] : null).filter(Boolean));
        return [...set].sort().reverse();
    }, [allEntries]);

    // Months available for the selected year (numeric strings "01"–"12")
    const availableMonths = useMemo(() => {
        const base = selectedYear === "all"
            ? allEntries
            : allEntries.filter((e) => e.date && e.date.startsWith(selectedYear));
        const set = new Set(base.map((e) => e.date ? e.date.split("-")[1] : null).filter(Boolean));
        return [...set].sort();
    }, [allEntries, selectedYear]);

    // Reset month when year changes
    const handleYearChange = (year) => {
        setSelectedYear(year);
        setSelectedMonth("all");
    };

    const toggleType = (type) => {
        setSelectedTypes((prev) =>
            prev.includes(type)
                ? prev.length === 1 ? prev : prev.filter((t) => t !== type)
                : [...prev, type]
        );
    };

    // Items shown in the list (all filters applied)
    const filtered = useMemo(() =>
        allEntries.filter((e) => {
            const yearOk = selectedYear === "all" || (e.date && e.date.startsWith(selectedYear));
            const monthOk = selectedMonth === "all" || (e.date && e.date.split("-")[1] === selectedMonth);
            const typeOk = !e.type || selectedTypes.includes(e.type);
            const highlightOk = showOnlyHighlights
                ? e.highlightLevel >= 1
                : showOnlyMega
                    ? e.highlightLevel === 2
                    : true;
            const dateStartOk = !dateRangeStart || (e.fullDate && e.fullDate >= dateRangeStart);
            const dateEndOk = !dateRangeEnd || (e.fullDate && e.fullDate <= dateRangeEnd);
            return yearOk && monthOk && typeOk && highlightOk && dateStartOk && dateEndOk;
        }),
        [allEntries, selectedYear, selectedMonth, selectedTypes, showOnlyHighlights, showOnlyMega, dateRangeStart, dateRangeEnd]
    );

    // For bar chart: only type filter (always shows full timeline)
    const filteredForBar = useMemo(() =>
        allEntries.filter((e) => !e.type || selectedTypes.includes(e.type)),
        [allEntries, selectedTypes]
    );

    // All months (YYYY-MM) for bar chart x-axis
    const allMonths = useMemo(() => {
        const set = new Set(allEntries.map((e) => e.date).filter(Boolean));
        return [...set].sort().reverse();
    }, [allEntries]);

    const groupedByVersion = useMemo(() => {
        const map = new Map();
        for (const entry of filtered) {
            if (!map.has(entry.version)) map.set(entry.version, []);
            map.get(entry.version).push(entry);
        }
        return [...map.entries()];
    }, [filtered]);

    const stats = useMemo(() => ({
        total: filtered.length,
        versions: groupedByVersion.length,
        added: filtered.filter((e) => e.type === "Added").length,
        changed: filtered.filter((e) => e.type === "Changed").length,
        fixed: filtered.filter((e) => e.type === "Fixed").length,
        support: filtered.filter((e) => e.type === "Support").length,
    }), [filtered, groupedByVersion]);

    return (
        <div className={classes.root}>

            {/* ── Header ── */}
            <div className={classes.header}>
                <span className={classes.title}>📋 Changelog</span>

                <div className={classes.headerDivider} />

                {/* Year filter */}
                <div className={classes.filterGroup}>
                    <span className={classes.filterLabel} style={{ margin: "0px 8px" }}>Ano</span>
                    <FormControl variant="outlined" size="small"
                        className={`${classes.periodSelect} ${classes.yearSelect}`}>
                        <Select
                            value={selectedYear}
                            onChange={(e) => handleYearChange(e.target.value)}
                        >
                            <MenuItem value="all">Todos</MenuItem>
                            {allYears.map((y) => (
                                <MenuItem key={y} value={y}>{y}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </div>

                {/* Month filter */}
                <div className={classes.filterGroup}>
                    <span className={classes.filterLabel} style={{ margin: "0px 8px" }}>Mês</span>
                    <FormControl variant="outlined" size="small"
                        className={`${classes.periodSelect} ${classes.monthSelect}`}>
                        <Select
                            value={selectedMonth}
                            onChange={(e) => setSelectedMonth(e.target.value)}
                        >
                            <MenuItem value="all">Todos</MenuItem>
                            {availableMonths.map((m) => (
                                <MenuItem key={m} value={m}>
                                    {MONTH_NAMES[parseInt(m, 10) - 1]}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </div>

                <div className={classes.headerDivider} />

                {/* Date range filter */}
                <div className={classes.filterGroup}>
                    <span className={classes.filterLabel} style={{ margin: "0px 8px" }}>De</span>
                    <TextField
                        type="date"
                        size="small"
                        variant="outlined"
                        className={classes.dateInput}
                        value={dateRangeStart}
                        onChange={(e) => setDateRangeStart(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <span className={classes.filterLabel} style={{ margin: "0px 8px" }}>Até</span>
                    <TextField
                        type="date"
                        size="small"
                        variant="outlined"
                        className={classes.dateInput}
                        value={dateRangeEnd}
                        onChange={(e) => setDateRangeEnd(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    {(dateRangeStart || dateRangeEnd) && (
                        <Tooltip title="Limpar filtro de datas">
                            <IconButton
                                size="small"
                                onClick={() => { setDateRangeStart(""); setDateRangeEnd(""); }}
                                style={{ marginLeft: 2 }}
                            >
                                <ClearIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </div>

                <div className={classes.headerDivider} />

                {/* Type filter */}
                <div className={classes.filterGroup}>
                    <span className={classes.filterLabel} style={{ margin: "0px 8px" }}>Tipo</span>
                    <div style={{ display: "flex", gap: "8px" }}>

                        {["Added", "Changed", "Fixed", "Support"].map((type) => (
                            <Chip
                                key={type}
                                className={classes.typeChip}
                                label={`${TYPE_ICONS[type]} ${type}`}
                                onClick={() => toggleType(type)}
                                style={{
                                    background: selectedTypes.includes(type) ? COLORS[type] : "#e8ecef",
                                    color: selectedTypes.includes(type) ? "white" : "#768191",
                                }}
                            />
                        ))}
                    </div>
                </div>

                <div className={classes.headerDivider} />

                {/* Highlights filter */}
                <div className={classes.filterGroup}>
                    <span className={classes.filterLabel} style={{ margin: "0px 8px" }}>Destaques</span>
                    <Chip
                        className={classes.highlightChip}
                        label="⭐ Destaques"
                        onClick={() => setShowOnlyHighlights((v) => !v)}
                        style={{
                            background: showOnlyHighlights ? "#f9a825" : "#e8ecef",
                            color: showOnlyHighlights ? "white" : "#768191",
                        }}
                    />
                    <Chip
                        className={classes.highlightChip}
                        label="🌟 Mega"
                        onClick={() => setShowOnlyMega((v) => !v)}
                        style={{
                            background: showOnlyMega ? "#f57f17" : "#e8ecef",
                            color: showOnlyMega ? "white" : "#768191",
                        }}
                    />
                </div>
            </div>

            {/* ── Body ── */}
            <div className={classes.body}>

                {/* Left: scrollable list */}
                <div className={classes.leftPanel}>
                    {groupedByVersion.length === 0 ? (
                        <div className={classes.noItems}>
                            Nenhuma entrada encontrada para os filtros selecionados.
                        </div>
                    ) : (
                        groupedByVersion.map(([version, items]) => (
                            <div className={classes.versionGroup} key={version}>
                                <div className={classes.versionBadge}>
                                    {version
                                        .replace(/[\[\]]/g, "")
                                        .replace(/(\d{2})-(\d{2})-(\d{4})/, "$1/$2/$3")
                                        .replace(/\s+(\d{2}\/\d{2}\/\d{4})/, " — $1")}
                                </div>
                                {items.map((item, idx) => {
                                    const color = item.type ? COLORS[item.type] : "#cbd5e0";
                                    const isMega = item.highlightLevel === 2;
                                    return (
                                        <div
                                            className={isMega
                                                ? classes.changeItemMega
                                                : item.highlightLevel === 1 ? classes.changeItemHighlighted : classes.changeItem}
                                            key={idx}
                                        >
                                            <div
                                                className={classes.itemSidebar}
                                                style={{ background: color }}
                                            />
                                            {item.type && (
                                                <span
                                                    className={classes.typePill}
                                                    style={{ background: color }}
                                                >
                                                    {item.type.toUpperCase()}
                                                </span>
                                            )}
                                            <span className={isMega ? `${classes.itemText} ${classes.itemTextMega}` : classes.itemText}>
                                                {stripTag(item.text)}
                                            </span>
                                            {item.highlightLevel > 0 && (
                                                <span
                                                    className={classes.highlightBadge}
                                                    title={isMega ? "Mega destaque" : "Destaque"}
                                                >
                                                    {isMega ? "🌟" : "⭐"}
                                                </span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        ))
                    )}
                </div>

                {/* Right: charts */}
                <div className={classes.rightPanel}>

                    {/* Stats row */}
                    <div className={classes.statsRow}>
                        {[
                            { label: "Versões", value: stats.versions, color: "#6c5ce7" },
                            { label: "Total", value: stats.total, color: unoTheme.mainColor },
                            { label: "Added", value: stats.added, color: COLORS.Added },
                            { label: "Changed", value: stats.changed, color: COLORS.Changed },
                            { label: "Fixed", value: stats.fixed, color: COLORS.Fixed },
                            { label: "Support", value: stats.support, color: COLORS.Support },
                        ].map(({ label, value, color }) => (
                            <div className={classes.statCard} key={label}>
                                <div className={classes.statNumber} style={{ color }}>{value}</div>
                                <div className={classes.statLabel}>{label}</div>
                            </div>
                        ))}
                    </div>

                    <DonutChart data={filtered} classes={classes} />
                    <BarChart data={filteredForBar} classes={classes} months={allMonths} />

                </div>
            </div>
        </div>
    );
}